import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Checkbox,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import {
  FilterBarActions,
  FilterBarAddButton,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { PolygonPerimeter } from '@evimo/types';
import { AreaBoundaryPicker } from '~/app/components/site-management/AreaBoundaryPicker';
import { SiteCoordinatePicker } from '~/app/components/site-management/SiteCoordinatePicker';
import { useTranslation } from 'react-i18next';
import type { ZoneLookup } from '~/app/services/site/site.service';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import {
  useZoneManagement,
  type ZoneFormValues,
} from '~/app/hooks/site/useZoneManagement';

const { Paragraph, Title, Text } = Typography;

export function ZoneManagement() {
  const { t } = useTranslation('sites');
  const [form] = Form.useForm<ZoneFormValues>();
  const selectedSiteId = Form.useWatch('siteId', form);
  const selectedLatitude = Form.useWatch('latitude', form);
  const selectedLongitude = Form.useWatch('longitude', form);
  const selectedPerimeter = Form.useWatch('perimeter', form);
  const {
    activeSiteId,
    editingZone,
    error,
    filteredZones,
    isDeleting,
    isFetching,
    isLoading,
    isModalOpen,
    isSubmitting,
    searchValue,
    siteNameById,
    sites,
    closeModal,
    deleteZone,
    openCreateModal,
    openEditModal,
    refetch,
    setSearchValue,
    setSelectedSiteId,
    submitZone,
  } = useZoneManagement();

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      name: editingZone?.name ?? '',
      siteId: editingZone?.siteId ?? activeSiteId ?? undefined,
      latitude:
        editingZone?.latitude === null || editingZone?.latitude === undefined
          ? undefined
          : String(editingZone.latitude),
      longitude:
        editingZone?.longitude === null || editingZone?.longitude === undefined
          ? undefined
          : String(editingZone.longitude),
      perimeter: editingZone?.perimeter ?? undefined,
      isMain: editingZone?.isMain ?? false,
    });
  }, [activeSiteId, editingZone, form, isModalOpen]);

  const handleDeleteZone = (zone: ZoneLookup): void => {
    Modal.confirm({
      title: t('actions.deleteZone', 'Delete zone'),
      content: t(
        'messages.deleteZoneConfirm',
        'Delete this zone from the selected site?',
      ),
      okText: t('actions.delete', 'Delete'),
      cancelText: t('actions.cancel', 'Cancel'),
      okButtonProps: { danger: true, loading: isDeleting },
      onOk: () => deleteZone(zone),
    });
  };

  const siteOptions = sites.map((site) => ({
    label: site.name,
    value: site.id,
  }));
  const selectedSite = sites.find((site) => site.id === selectedSiteId);

  const handleCoordinatesChange = useCallback(
    (coordinates: { latitude: string; longitude: string }) => {
      form.setFieldsValue({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      });
    },
    [form],
  );

  const handlePerimeterChange = useCallback(
    (perimeter: PolygonPerimeter | null) => {
      form.setFieldValue('perimeter', perimeter);
    },
    [form],
  );

  const referenceCoordinates =
    selectedLatitude && selectedLongitude
      ? {
          latitude: Number(selectedLatitude),
          longitude: Number(selectedLongitude),
        }
      : selectedSite?.latitude !== null &&
          selectedSite?.latitude !== undefined &&
          selectedSite?.longitude !== null &&
          selectedSite?.longitude !== undefined
        ? {
            latitude: selectedSite.latitude,
            longitude: selectedSite.longitude,
          }
        : null;
  const addressQuery = [
    selectedSite?.address?.trim(),
    selectedSite?.city?.name,
    selectedSite?.province?.name,
    'France',
  ]
    .filter(Boolean)
    .join(', ');
  const isAreaPickerDisabled =
    !addressQuery &&
    !referenceCoordinates &&
    !selectedPerimeter?.length &&
    !selectedSite?.perimeter?.length;

  const columns: ColumnsType<ZoneLookup> = [
    {
      title: t('zones.table.name', 'Zone name'),
      dataIndex: 'name',
      key: 'name',
      render: (_value, zone) => (
        <Link to={`/sites/${zone.siteId}/zones/${zone.id}`}>{zone.name}</Link>
      ),
    },
    {
      title: t('zones.table.site', 'Site'),
      dataIndex: 'siteId',
      key: 'siteId',
      render: (siteId: string) => siteNameById.get(siteId) ?? '-',
    },
    {
      title: (
        <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {t('zones.table.isMain', 'Main')}
        </span>
      ),
      dataIndex: 'isMain',
      key: 'isMain',
      align: 'center',
      width: 88,
      render: (value: boolean) => (
        <Flex align="center" justify="center">
          <span
            aria-label={
              value
                ? t('zones.table.mainZone', 'Main zone')
                : t('zones.table.secondaryZone', 'Secondary zone')
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: value ? '#16a34a' : '#ffffff',
              color: '#ffffff',
              border: value ? '1px solid #16a34a' : '1px solid #cbd5e1',
              boxShadow: value ? '0 4px 10px rgba(22, 163, 74, 0.18)' : 'none',
            }}
          >
            {value ? <CheckOutlined style={{ fontSize: 13 }} /> : null}
          </span>
        </Flex>
      ),
    },
    {
      title: t('zones.table.latitude', 'Latitude'),
      dataIndex: 'latitude',
      key: 'latitude',
      render: (value: number | null) => value ?? '-',
    },
    {
      title: t('zones.table.longitude', 'Longitude'),
      dataIndex: 'longitude',
      key: 'longitude',
      render: (value: number | null) => value ?? '-',
    },
    {
      title: t('zones.table.actions', 'Actions'),
      key: 'actions',
      render: (_value, zone) => {
        return (
          <TableRowActionsMenu
            ariaLabel={t('actions.more', 'More actions')}
            items={[
              {
                key: 'edit',
                label: t('actions.edit', 'Edit'),
                onClick: () => openEditModal(zone),
              },
              {
                key: 'delete',
                label: t('actions.delete', 'Delete'),
                danger: true,
                onClick: () => handleDeleteZone(zone),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('zones.title', 'Zones')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'zones.description',
            'Manage zones for the sites belonging to the current organization workspace.',
          )}
        </Paragraph>
      </div>

      <EvimoListPanel>
        <FilterBar>
          <Select
            value={activeSiteId || undefined}
            options={siteOptions}
            placeholder={t('zones.siteFilterPlaceholder', 'Select a site')}
            onChange={setSelectedSiteId}
          />
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t('zones.searchPlaceholder', 'Search zones')}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />

          <FilterBarActions>
            <FilterBarRefreshButton
              ns="sites"
              labelKey="actions.refresh"
              loading={isFetching}
              onClick={() => void refetch()}
            />
            <FilterBarAddButton
              ns="sites"
              labelKey="zones.actions.addZone"
              defaultLabel="Add zone"
              disabled={!sites.length}
              onClick={openCreateModal}
            />
          </FilterBarActions>
        </FilterBar>

        {!sites.length ? (
          <Text
            type="secondary"
            style={{ display: 'block', padding: '12px 16px', margin: 0 }}
          >
            {t(
              'zones.noSites',
              'Create at least one site before managing zones.',
            )}
          </Text>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            style={{ margin: '12px 16px' }}
            message={t('zones.loadFailed', 'Failed to load zones.')}
            description={error.message}
          />
        ) : null}

        <EvimoTable<ZoneLookup>
          columns={columns}
          dataSource={filteredZones}
          loading={isLoading}
          rowKey="id"
          stickyLastColumn
          pagination={false}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => {
              window.location.href = `/sites/${record.siteId}/zones/${record.id}`;
            },
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: t(
              'zones.empty',
              'No zones found for the selected site.',
            ),
          }}
        />
      </EvimoListPanel>

      <Modal
        destroyOnHidden
        open={isModalOpen}
        title={
          editingZone
            ? t('zones.modal.editTitle', 'Edit zone')
            : t('zones.modal.createTitle', 'Create zone')
        }
        okText={
          editingZone
            ? t('actions.save', 'Save')
            : t('actions.create', 'Create')
        }
        cancelText={t('actions.cancel', 'Cancel')}
        confirmLoading={isSubmitting}
        onCancel={closeModal}
        onOk={() => void form.submit()}
      >
        <Form<ZoneFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) =>
            void submitZone({
              ...values,
              perimeter: selectedPerimeter ?? null,
            })
          }
        >
          <Form.Item
            name="siteId"
            label={t('zones.fields.site', 'Site')}
            rules={[
              {
                required: true,
                message: t('zones.validation.site', 'Site is required.'),
              },
            ]}
          >
            <Select
              options={siteOptions}
              onChange={(value) => {
                if (value !== selectedSiteId) {
                  form.setFieldsValue({
                    latitude: undefined,
                    longitude: undefined,
                    perimeter: undefined,
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('zones.fields.name', 'Zone name')}
            rules={[
              {
                required: true,
                message: t('zones.validation.name', 'Zone name is required.'),
              },
            ]}
          >
            <Input
              placeholder={t('zones.placeholders.name', 'Enter the zone name')}
            />
          </Form.Item>
          <Form.Item
            label={t('zones.fields.locationPicker', 'Location on map')}
          >
            <SiteCoordinatePicker
              address={selectedSite?.address ?? ''}
              cityName={selectedSite?.city?.name}
              provinceName={selectedSite?.province?.name}
              latitude={selectedLatitude}
              longitude={selectedLongitude}
              selectionRequiredText={t(
                'zones.messages.pickLocationRequiresSite',
                'Select a site with city and province data to enable the map picker.',
              )}
              instructionsText={t(
                'zones.messages.pickLocationInstructions',
                'Once the map is centered on the selected site area, click the exact zone position to latitude and longitude.',
              )}
              currentCoordinatesText={t(
                'messages.currentCoordinates',
                'Selected coordinates',
              )}
              missingKeyText={t(
                'messages.mapMissingKey',
                'Google Maps is not configured for this environment.',
              )}
              geocodeFailedText={t(
                'zones.messages.mapGeocodeFailed',
                'The selected site location could not be centered on the map.',
              )}
              loadFailedText={t(
                'messages.mapLoadFailed',
                'The map could not be loaded right now.',
              )}
              locatingText={t(
                'zones.messages.mapLocating',
                'Centering the map on the selected site...',
              )}
              onCoordinatesChange={handleCoordinatesChange}
            />
          </Form.Item>
          <Form.Item label={t('zones.fields.perimeter', 'Coverage perimeter')}>
            <AreaBoundaryPicker
              addressQuery={addressQuery || null}
              perimeter={selectedPerimeter}
              overlayPerimeter={selectedSite?.perimeter ?? null}
              referenceCoordinates={referenceCoordinates}
              disabled={isAreaPickerDisabled}
              selectionRequiredText={t(
                'zones.messages.pickPerimeterRequiresSite',
                'Select a site with map context or coordinates to enable the perimeter editor.',
              )}
              instructionsText={t(
                'zones.messages.pickPerimeterInstructions',
                'Click the map to add zone perimeter points. The site perimeter is shown in the background when available. Add at least three points, then drag the polygon handles to refine the zone area.',
              )}
              currentPerimeterText={t(
                'messages.currentPerimeterPoints',
                'Perimeter points',
              )}
              missingKeyText={t(
                'messages.mapMissingKey',
                'Google Maps is not configured for this environment.',
              )}
              geocodeFailedText={t(
                'zones.messages.mapGeocodeFailed',
                'The selected site location could not be centered on the map.',
              )}
              loadFailedText={t(
                'messages.mapLoadFailed',
                'The map could not be loaded right now.',
              )}
              locatingText={t(
                'zones.messages.mapLocating',
                'Centering the map on the selected site...',
              )}
              clearText={t('actions.clearPerimeter', 'Clear perimeter')}
              onPerimeterChangeAction={handlePerimeterChange}
            />
          </Form.Item>
          <Form.Item
            name="latitude"
            label={t('zones.fields.latitude', 'Latitude')}
          >
            <Input
              inputMode="decimal"
              placeholder={t(
                'zones.placeholders.latitude',
                'Enter the zone latitude',
              )}
            />
          </Form.Item>
          <Form.Item
            name="longitude"
            label={t('zones.fields.longitude', 'Longitude')}
          >
            <Input
              inputMode="decimal"
              placeholder={t(
                'zones.placeholders.longitude',
                'Enter the zone longitude',
              )}
            />
          </Form.Item>
          <Form.Item name="isMain" valuePropName="checked">
            <Checkbox>{t('zones.fields.isMain', 'Main zone')}</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
